import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/** What the vendor grants, named for what it unlocks rather than for an OAuth scope. */
export type CommerceGrant = 'catalog' | 'sales' | 'stock' | 'customers' | 'publish';

export type CommercePlatform =
    | 'shopify'
    | 'woocommerce'
    | 'prestashop'
    | 'magento'
    | 'bigcommerce'
    | 'wix'
    | 'squarespace';

export interface PlatformDetection {
    platform: CommercePlatform | null;
    /** `certain` states it; `likely` asks. Being wrong confidently is worse than asking. */
    confidence: 'certain' | 'likely' | null;
    evidence: string[];
    connectable: boolean;
    websiteUrl: string | null;
    alreadyConnected: boolean;
}

export interface ShopConnection {
    id: string;
    platform: CommercePlatform;
    shopDomain: string;
    shopName?: string;
    currency?: string;
    grants: CommerceGrant[];
    status: 'active' | 'error' | 'revoked';
    lastError?: string;
    lastSyncAt?: string;
    backfill: {
        status: 'pending' | 'running' | 'done' | 'failed';
        ordersImported: number;
        productsImported: number;
        from?: string;
        finishedAt?: string;
    };
}

export interface RevenueProductRow {
    productUuid: string | null;
    sku: string | null;
    title: string | null;
    revenue: number;
    units: number;
    orders: number;
    /** False means they sell it and we cannot show it — the actionable row. */
    inArdealy: boolean;
}

export interface RevenueReport {
    connected: boolean;
    currency: string | null;
    period: { from: string; to: string };
    revenue: number;
    orders: number;
    returningOrders: number;
    averageOrder: number;
    byProduct: RevenueProductRow[];
    byChannel: Array<{ channel: string; revenue: number; orders: number }>;
    catalogue: { shopProducts: number; linked: number; unlinked: number };
}

const BASE = '/services/pms/vendors/me/commerce';

export const useCommerceApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const detect = useCallback(
        async (): Promise<PlatformDetection> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/detect`)) as PlatformDetection,
        [sendAuthGuardedRequest],
    );

    const connections = useCallback(
        async (): Promise<ShopConnection[]> =>
            ((await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/connections`)) ?? []) as ShopConnection[],
        [sendAuthGuardedRequest],
    );

    const connect = useCallback(
        async (body: {
            platform: CommercePlatform;
            shopDomain: string;
            credentials: Record<string, string>;
            grants: CommerceGrant[];
        }): Promise<ShopConnection> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/connections`, body)) as ShopConnection,
        [sendAuthGuardedRequest],
    );

    const sync = useCallback(
        async (id: string): Promise<{ orders: number; products: number; done: boolean }> =>
            (await sendAuthGuardedRequest(ApiMethod.POST, `${BASE}/connections/${id}/sync`, {})) as {
                orders: number;
                products: number;
                done: boolean;
            },
        [sendAuthGuardedRequest],
    );

    const disconnect = useCallback(
        async (id: string): Promise<{ disconnected: boolean }> =>
            (await sendAuthGuardedRequest(ApiMethod.DELETE, `${BASE}/connections/${id}`)) as { disconnected: boolean },
        [sendAuthGuardedRequest],
    );

    const revenue = useCallback(
        async (range?: { from?: string; to?: string }): Promise<RevenueReport> => {
            const query = new URLSearchParams();
            if (range?.from) query.set('from', range.from);
            if (range?.to) query.set('to', range.to);
            const suffix = query.toString() ? `?${query}` : '';
            return (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/revenue${suffix}`)) as RevenueReport;
        },
        [sendAuthGuardedRequest],
    );

    // Memoised: `useShopConnection` depends on this object, and a fresh one
    // each render would make its loader a new function every render too.
    return useMemo(
        () => ({ detect, connections, connect, sync, disconnect, revenue }),
        [detect, connections, connect, sync, disconnect, revenue],
    );
};

/**
 * The connection state a page needs, loaded once.
 *
 * Detection and the connection list are always wanted together — a page that
 * knew one without the other would either offer a shop already connected or
 * show an empty state to someone who has one.
 */
export const useShopConnection = () => {
    const api = useCommerceApi();
    const [detection, setDetection] = useState<PlatformDetection | null>(null);
    const [shops, setShops] = useState<ShopConnection[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Settled rather than all: detection reaches out to their website
            // and may be the slow one, and a connected shop should still show
            // when their site is down.
            const [detected, list] = await Promise.allSettled([api.detect(), api.connections()]);
            if (detected.status === 'fulfilled') setDetection(detected.value);
            if (list.status === 'fulfilled') setShops(list.value ?? []);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { detection, shops, loading, reload: load };
};
